import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixMembersV1SantaMember from './members-v1-santa-member-santa-member.http.js';

/** Fake FQDN to comply with the rules */
export interface SantaMember {
  /**
   * fake id
   * @format GUID
   */
  _id?: string | null;
}

export interface GetMyMemberPageRequest {
  /** My member page config */
  config?: Config;
  /** My member page full url */
  fullUrl?: string;
  /** My member page roles */
  pageRoles?: Record<string, PageRole>;
  /** My member page request info */
  requestInfo?: RequestInfo;
  /** My member page router prefix */
  routerPrefix?: string;
  /** My member page router suffix */
  routerSuffix?: string;
}

export interface Config {
  /** Configuration patterns */
  patterns?: Record<string, Pattern>;
  /** Configuration type */
  type?: string;
}

export interface Pattern {
  /** Pattern app data */
  appData?: AppData;
  /**
   * Pattern page id
   * @format GUID
   */
  page?: string;
  /** Pattern seo data */
  seoData?: SeoData;
  /** Pattern social home flag */
  socialHome?: boolean;
  /** Pattern page title */
  title?: string;
}

export interface AppData {
  /** App definition id */
  appDefinitionId?: string;
  /** App page id */
  appPageId?: string;
  /** App menu order */
  menuOrder?: number;
  /** App numbers */
  numbers?: Numbers;
  /** Which roles app is visible to */
  visibleForRoles?: string[] | null;
}

export interface Numbers {
  /** Number default value */
  default?: number;
  /** Number key */
  key?: string;
}

export interface SeoData {
  /** SEO description */
  description?: string | null;
  /** SEO keywords */
  keywords?: string | null;
  /** SEO no index flag */
  noIndex?: string;
  /** SEO title */
  title?: string;
}

export interface PageRole {
  /** Page role id */
  _id?: string;
  /** Page title */
  title?: string;
}

export interface RequestInfo {
  /** Request info environment */
  env?: string;
  /** Request form factor */
  formFactor?: string;
}

export interface GetMyMemberPageResponse {
  /** Get mt member page result */
  result?: MemberPageResult;
}

export interface MemberPageResult {
  /** Data Struct can return types of FullData or RolesData messages. */
  data?: Record<string, any> | null;
  /** Page head */
  head?: Head;
  /**
   * A message
   * @maxLength 255
   */
  message?: string | null;
  /**
   * Page id
   * @maxLength 255
   */
  page?: string;
  /** Public data */
  publicData?: PublicData;
  /**
   * Page redirect url
   * @maxLength 255
   */
  redirectUrl?: string | null;
  /** Page status */
  status?: number;
  /**
   * Page tpa inner route
   * @maxLength 255
   */
  tpaInnerRoute?: string | null;
}

export interface Head {
  /**
   * Head description
   * @maxLength 255
   */
  description?: string | null;
  /**
   * Head keywords
   * @maxLength 255
   */
  keywords?: string | null;
  /** Head meta tags */
  metaTags?: Record<string, string>;
  /**
   * Head no index flag
   * @maxLength 255
   */
  noIndex?: string;
  /**
   * Head title
   * @maxLength 255
   */
  title?: string | null;
}

export interface PublicData {
  /** Viewed member */
  viewedUser?: ViewedUser;
}

export interface ViewedUser {
  /**
   * Viewed member id
   * @format GUID
   */
  _id?: string;
  /**
   * Viewed member name
   * @maxLength 255
   */
  name?: string | null;
  /**
   * Viewed member roles
   * @maxLength 255
   * @maxSize 10
   */
  roles?: string[];
  /**
   * Viewed member slug
   * @maxLength 255
   */
  slug?: string | null;
}

export interface GetSiteMapRequest {
  /** Get site map configuration */
  config?: Config;
  /** Get site map url of the page */
  fullUrl?: string;
  /** Get site map page roles */
  pageRoles?: Record<string, PageRole>;
  /** Get site map request info */
  requestInfo?: RequestInfo;
  /** Get site map router prefix */
  routerPrefix?: string;
  /** Get site map router suffix */
  routerSuffix?: string;
}

export interface GetSiteMapResponse {
  /** Site map result */
  result?: any;
}

/**
 * Returns my member page
 * @public
 * @documentationMaturity preview
 * @permissionId MEMBERS.SANTA_MEMBERS_MY_MEMBER_PAGE_READ
 * @applicableIdentity APP
 * @fqn com.wixpress.members.santamembers.SantaMembers.GetMyMemberPage
 */
export async function getMyMemberPage(
  options?: GetMyMemberPageOptions
): Promise<
  NonNullablePaths<
    GetMyMemberPageResponse,
    | `result.head.noIndex`
    | `result.page`
    | `result.publicData.viewedUser._id`
    | `result.publicData.viewedUser.roles`
    | `result.status`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    config: options?.config,
    fullUrl: options?.fullUrl,
    pageRoles: options?.pageRoles,
    requestInfo: options?.requestInfo,
    routerPrefix: options?.routerPrefix,
    routerSuffix: options?.routerSuffix,
  });

  const reqOpts = ambassadorWixMembersV1SantaMember.getMyMemberPage(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          config: '$[0].config',
          fullUrl: '$[0].fullUrl',
          pageRoles: '$[0].pageRoles',
          requestInfo: '$[0].requestInfo',
          routerPrefix: '$[0].routerPrefix',
          routerSuffix: '$[0].routerSuffix',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface GetMyMemberPageOptions {
  /** My member page config */
  config?: Config;
  /** My member page full url */
  fullUrl?: string;
  /** My member page roles */
  pageRoles?: Record<string, PageRole>;
  /** My member page request info */
  requestInfo?: RequestInfo;
  /** My member page router prefix */
  routerPrefix?: string;
  /** My member page router suffix */
  routerSuffix?: string;
}

/**
 * Returns site map
 * No permission as the request can be made by anything pretty much
 * @public
 * @documentationMaturity preview
 * @fqn com.wixpress.members.santamembers.SantaMembers.GetSiteMap
 */
export async function getSiteMap(
  options?: GetSiteMapOptions
): Promise<GetSiteMapResponse> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    config: options?.config,
    fullUrl: options?.fullUrl,
    pageRoles: options?.pageRoles,
    requestInfo: options?.requestInfo,
    routerPrefix: options?.routerPrefix,
    routerSuffix: options?.routerSuffix,
  });

  const reqOpts = ambassadorWixMembersV1SantaMember.getSiteMap(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          config: '$[0].config',
          fullUrl: '$[0].fullUrl',
          pageRoles: '$[0].pageRoles',
          requestInfo: '$[0].requestInfo',
          routerPrefix: '$[0].routerPrefix',
          routerSuffix: '$[0].routerSuffix',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface GetSiteMapOptions {
  /** Get site map configuration */
  config?: Config;
  /** Get site map url of the page */
  fullUrl?: string;
  /** Get site map page roles */
  pageRoles?: Record<string, PageRole>;
  /** Get site map request info */
  requestInfo?: RequestInfo;
  /** Get site map router prefix */
  routerPrefix?: string;
  /** Get site map router suffix */
  routerSuffix?: string;
}
